import { CartItem } from "@/components/cart"
import { FeedbackModal } from "@/components/feedback"
import { CartSuggestionsStrip } from "@/components/suggestions/CartSuggestionsStrip"
import { useAnimatedItemCount } from "@/hooks/use-animated-currency"
import { useBagCoupons } from "@/hooks/use-bag-coupons"
import { useCartPrizes } from "@/hooks/use-cart-prizes"
import { useOptimisticCart } from "@/hooks/use-optimistic-cart"
import { bagCouponDiscountCents } from "@/lib/bag-coupon"
import { CART_LINE_PARAM } from "@/lib/cart-line-highlight"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import AddMoreItemsButton from "./AddMoreItemsButton"
import { CartPrizes } from "./CartPrizes"
import { BagCouponStrip } from "./coupon/BagCouponStrip"
import FinishCartButton from "./FinishCartButton"
import { BagSummary } from "./summary/BagSummary"
import { Badge, Button, Card, Heading, PageContainer, Text } from "@/components/ui"
import { TRANSITION } from "@/lib/motion"

const MotionCard = motion.create(Card)

/** The bag with lines: header, attached coupons and prizes, the lines, suggestions, the value summary and the footer CTA. */
export default function FilledCart() {
  const { cart, clearCart } = useOptimisticCart()
  const [clearModalOpen, setClearModalOpen] = useState(false)
  const [searchParams] = useSearchParams()
  const highlightedLineId = searchParams.get(CART_LINE_PARAM)
  const highlightedRef = useRef<HTMLElement | null>(null)
  const reduceMotion = useReducedMotion()
  const highlightedInBag = cart.items.some((item) => item.id === highlightedLineId)

  useEffect(() => {
    const line = highlightedRef.current
    if (!highlightedInBag || !line) return
    line.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" })
    line.focus({ preventScroll: true })
  }, [highlightedInBag, reduceMotion])

  function handleClearCart() {
    clearCart()
    setClearModalOpen(false)
  }

  const itemCount = cart.items.reduce((acc, item) => acc + item.quantity, 0)
  const itemCountLabel = useAnimatedItemCount(itemCount)
  const prizes = useCartPrizes(cart)
  const bagCoupons = useBagCoupons(cart)
  const discountCents = prizes.discountCents + bagCouponDiscountCents(bagCoupons.coupons)
  const payable = Math.max(0, cart.totalPrice - discountCents / 100)

  return (
    <>
      <motion.div
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={TRANSITION.enter}
      >
        <PageContainer top="gap" bottom="chrome">
          <header className="mb-4 flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <Heading level={1}>Itens</Heading>
              <Badge size="md" shape="pill" weight="semibold">
                <motion.span className="tabular-nums">{itemCountLabel}</motion.span>
              </Badge>
            </div>
            <Button
              variant="link"
              tone="neutral"
              size="auto"
              weight="normal"
              hoverTone="danger"
              onClick={() => setClearModalOpen(true)}
            >
              <Text as="span" size="base">
                Limpar
              </Text>
            </Button>
            <FeedbackModal
              open={clearModalOpen}
              onOpenChange={setClearModalOpen}
              variant="warning"
              confirmTone="danger"
              title="Esvaziar sacola?"
              description="Todos os itens vão sair da sacola. Não dá pra desfazer essa ação."
              confirmLabel="Esvaziar sacola"
              cancelLabel="Voltar"
              onConfirm={handleClearCart}
            />
          </header>

          <BagCouponStrip coupons={bagCoupons.coupons} ready={bagCoupons.ready} />

          <CartPrizes prizes={prizes.prizes} />

          <div className="flex flex-col gap-3">
            <ul className="flex flex-col gap-3">
              <AnimatePresence initial={false} mode="popLayout">
                {cart.items.map((cartItem, index) => {
                  const highlighted = cartItem.id === highlightedLineId
                  return (
                    <MotionCard
                      as="li"
                      key={cartItem.id}
                      ref={highlighted ? highlightedRef : undefined}
                      tabIndex={highlighted ? -1 : undefined}
                      aria-describedby={highlighted ? `coupon-hint-${cartItem.id}` : undefined}
                      data-testid="cart-item"
                      highlight={highlighted}
                      tone={highlighted ? "warning" : "neutral"}
                      padding="sm"
                      className="outline-none"
                      layout={reduceMotion ? false : "position"}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0, transition: TRANSITION.enter }}
                      exit={{ opacity: 0, scale: 0.97, transition: TRANSITION.exit }}
                      transition={TRANSITION.size}
                    >
                      {highlighted ? (
                        <Text id={`coupon-hint-${cartItem.id}`} size="sm" weight="semibold" tone="default" className="mb-2">
                          Ajuste este item para o seu cupom valer.
                        </Text>
                      ) : null}
                      <CartItem cartItem={cartItem} prizeDiscountCents={prizes.lineDiscountCents[index]} />
                    </MotionCard>
                  )
                })}
              </AnimatePresence>
            </ul>

            <AddMoreItemsButton />
          </div>

          <CartSuggestionsStrip cartItems={cart.items} />

          <div className="mt-6">
            <BagSummary subtotal={cart.totalPrice} discountCents={discountCents} payable={payable} />
          </div>
        </PageContainer>
      </motion.div>
      <FinishCartButton cart={cart} totalPrice={payable} />
    </>
  )
}
